const MM_TO_PX = 96 / 25.4;
const PAGE_HEIGHT_MM = 280;
const PAGE_PADDING_TOP_MM = 53;
const PAGE_PADDING_BOTTOM_MM = 12;
const CONTENT_HEIGHT_PX = (PAGE_HEIGHT_MM - PAGE_PADDING_TOP_MM - PAGE_PADDING_BOTTOM_MM) * MM_TO_PX;

const measureHeight = (element) => {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    return rect.height || 0;
};

const createMeasurer = (sourceRoot) => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-10000px;top:0;width:242mm;visibility:hidden;pointer-events:none;';
    const clone = sourceRoot.cloneNode(true);
    host.appendChild(clone);
    document.body.appendChild(host);
    return { host, clone };
};

const destroyMeasurer = (host) => {
    if (host && host.parentNode) {
        host.parentNode.removeChild(host);
    }
};

const measureSignaturesHeight = (host, showReceivedBy) => {
    const probe = document.createElement('div');
    probe.className = 'print-last-page-footer';
    probe.innerHTML = `
        <div class="print-signatures">
            <div class="sig-block sig-left">
                <div class="sig-line"></div>
                <div class="sig-label">AUTHORIZED BY</div>
            </div>
            ${showReceivedBy ? `
            <div class="sig-block sig-right">
                <div class="sig-line"></div>
                <div class="sig-label">RECEIVED BY</div>
            </div>` : ''}
        </div>
    `;
    host.appendChild(probe);
    const height = measureHeight(probe.querySelector('.print-signatures'));
    host.removeChild(probe);
    return height;
};

/**
 * Split print source into fixed-height pages and inject footers / page numbers.
 */
export const paginateForPrint = (sourceRoot, options = {}) => {
    const { showReceivedBy = false } = options;
    const { host, clone } = createMeasurer(sourceRoot);

    try {
        const header = clone.querySelector('.print-page-header');
        const table = clone.querySelector('#items .print-table');
        const tbody = table?.querySelector('tbody');
        const rows = tbody ? Array.from(tbody.querySelectorAll('tr')) : [];
        const colgroupHtml = table?.querySelector('colgroup')?.outerHTML || '';
        const theadHtml = table?.querySelector('thead')?.outerHTML || '';
        const bodyClass = clone.querySelector('#container')?.className || '';
        const footerTemplate = clone.querySelector('.print-last-page-footer');
        const footerHtml = footerTemplate ? footerTemplate.innerHTML : '';
        const tableClass = table?.className || 'print-table';

        const headerHeight = measureHeight(header);
        const totalsHeight = footerTemplate ? measureHeight(footerTemplate) : 0;
        const signaturesHeight = measureSignaturesHeight(host, showReceivedBy);
        const footerHeight = totalsHeight + signaturesHeight;
        const rowHeights = rows.map((row) => measureHeight(row));

        const theadProbe = document.createElement('div');
        theadProbe.style.width = '242mm';
        theadProbe.innerHTML = `<table class="${tableClass}">${colgroupHtml}${theadHtml}<tbody><tr>
            <td class="col-item">&nbsp;</td>
            <td class="col-qty">&nbsp;</td>
            <td class="col-unit">&nbsp;</td>
            <td class="col-price">&nbsp;</td>
            <td class="col-amt">&nbsp;</td>
        </tr></tbody></table>`;
        host.appendChild(theadProbe);
        const theadHeight = measureHeight(theadProbe.querySelector('thead'));
        host.removeChild(theadProbe);

        const pageIndicatorReserve = 20;
        const pages = [];
        let rowIndex = 0;
        let pageNum = 0;

        while (rowIndex < rows.length || pageNum === 0) {
            pageNum += 1;
            const isFirst = pageNum === 1;
            const usedHeader = isFirst ? headerHeight : 0;
            let available = CONTENT_HEIGHT_PX - usedHeader - theadHeight;
            const pageRows = [];

            if (rowIndex >= rows.length) {
                if (pageNum === 1) break;
                break;
            }

            while (rowIndex < rows.length) {
                const isLastRow = rowIndex === rows.length - 1;
                const rowH = rowHeights[rowIndex];
                const needsFooter = isLastRow && footerHtml;
                const required = rowH + (needsFooter ? footerHeight + pageIndicatorReserve : 0);

                if (pageRows.length > 0 && required > available) break;
                if (pageRows.length === 0 && rowH + theadHeight + (needsFooter ? footerHeight : 0) > CONTENT_HEIGHT_PX - usedHeader) {
                    pageRows.push(rows[rowIndex].outerHTML);
                    rowIndex += 1;
                    break;
                }
                if (rowH > available && pageRows.length === 0) {
                    pageRows.push(rows[rowIndex].outerHTML);
                    rowIndex += 1;
                    break;
                }
                if (rowH <= available) {
                    pageRows.push(rows[rowIndex].outerHTML);
                    available -= rowH;
                    rowIndex += 1;
                    if (isLastRow) break;
                } else {
                    break;
                }
            }

            pages.push({
                isFirst,
                isLast: rowIndex >= rows.length,
                headerHtml: isFirst && header ? header.outerHTML : '',
                colgroupHtml,
                theadHtml,
                rowsHtml: pageRows.join(''),
                tableClass,
            });
        }

        if (pages.length === 0 || (rows.length === 0 && pages.every((p) => !p.rowsHtml))) {
            pages.length = 0;
            pages.push({
                isFirst: true,
                isLast: true,
                headerHtml: header ? header.outerHTML : '',
                colgroupHtml,
                theadHtml,
                rowsHtml: rows.length > 0 ? rows.map((r) => r.outerHTML).join('') : '',
                tableClass,
            });
        } else {
            pages[pages.length - 1].isLast = true;
        }

        const totalPages = pages.length;
        return pages.map((page, idx) => {
            const pageNumber = idx + 1;
            const signaturesHtml = page.isLast ? `
                <div class="print-signatures">
                    <div class="sig-block sig-left">
                        <div class="sig-line"></div>
                        <div class="sig-label">AUTHORIZED BY</div>
                    </div>
                    ${showReceivedBy ? `
                    <div class="sig-block sig-right">
                        <div class="sig-line"></div>
                        <div class="sig-label">RECEIVED BY</div>
                    </div>` : ''}
                </div>
            ` : '';

            const footerBlock = page.isLast ? `
                <div class="print-last-page-footer">
                    ${footerHtml}
                    ${signaturesHtml}
                </div>
            ` : '';

            const pageIndicator = totalPages > 1
                ? `<div class="page-indicator">PAGE ${pageNumber} OF ${totalPages}</div>`
                : '';

            return `
                <div class="print-page">
                    <div id="container" class="${bodyClass}">
                        <div class="print-page-body">
                            ${page.headerHtml}
                            <section id="items">
                                <table class="${page.tableClass}" cellspacing="0" cellpadding="0">
                                    ${page.colgroupHtml || ''}
                                    ${page.theadHtml}
                                    <tbody>${page.rowsHtml}</tbody>
                                </table>
                            </section>
                            ${footerBlock}
                            <div class="print-page-spacer"></div>
                            ${pageIndicator}
                        </div>
                    </div>
                </div>
            `;
        }).join('');
    } finally {
        destroyMeasurer(host);
    }
};

export const buildPrintHtml = (sourceRoot, options = {}) => {
    if (!sourceRoot) return '';
    const pagesHtml = paginateForPrint(sourceRoot, options);
    return `<div class="print-document">${pagesHtml}</div>`;
};
